import { MercyManager } from '@antification/core';
import { Camera } from './Camera';

/**
 * What mercy looks like from above, which is almost nothing.
 *
 * The three builds in §10 have a rendering problem the rest of the game does not: a
 * covenanted meadow looks exactly like a meadow, a sapling looks like grass until it
 * is a bush, and an undercrossing is a hole under a road nobody can see into. Law 2
 * — *a consequence has to be visible somewhere* — leaves no choice about drawing
 * something, and the register leaves very little choice about what.
 *
 * So: three small marks, in the palette the surface already uses, at a scale that
 * disappears when you zoom out. A covenant is a boundary stone at the corner of the
 * cell. A sapling is three thin stems, which the ordinary bush sprite replaces the
 * moment it comes in — the renderer stops drawing it because `MercyManager` stops
 * listing it, and nothing had to coordinate that. A crossing is a pale arch across
 * the pavement: the only mark on this map that stands for something happening
 * underneath it.
 *
 * Deliberately absent: any indication of whether any of it worked. There is no
 * counter over a crossing, no glow on a fed hedgerow, no tally anywhere. §12's
 * federal overlay is where covenant stones eventually get a proper toggled layer
 * (Phase 4); until then these are always on and always quiet.
 */
export class MercyRenderer {
  render(ctx: CanvasRenderingContext2D, camera: Camera, mercy: MercyManager): void {
    if (mercy.deededCells === 0 && mercy.saplingCells === 0 && mercy.crossingCells === 0) {
      return;
    }

    const scale = camera.getScale();
    const cellW = camera.cellWidth * scale;
    const cellH = camera.cellHeight * scale;

    ctx.save();

    // The crossings first, flat on the pavement, so a stone or a stem on a
    // neighbouring cell draws over the arch rather than under it.
    ctx.strokeStyle = 'rgba(96, 116, 104, 0.55)';
    ctx.lineWidth = Math.max(1, 1.6 * scale);
    for (const cell of mercy.crossed()) {
      const p = camera.gridToScreen(cell.x, cell.y);
      // An arch across the short axis of the cell: a culvert mouth, seen from above
      // at the angle everything else here is seen from.
      ctx.beginPath();
      ctx.moveTo(p.x - cellW * 0.26, p.y + cellH * 0.1);
      ctx.quadraticCurveTo(p.x, p.y - cellH * 0.22, p.x + cellW * 0.26, p.y + cellH * 0.1);
      ctx.stroke();
    }

    // The covenant stones. One per cell, at the cell's south corner, which is the
    // point of the diamond nearest the viewer and the only one a neighbouring house
    // cannot hide.
    for (const cell of mercy.deeded()) {
      const p = camera.gridToScreen(cell.x, cell.y);
      const w = Math.max(1.4, 2.2 * scale);
      const h = Math.max(2.2, 4.2 * scale);
      const baseY = p.y + cellH * 0.22;

      ctx.fillStyle = 'rgba(120, 126, 118, 0.9)';
      ctx.beginPath();
      ctx.moveTo(p.x - w, baseY);
      ctx.lineTo(p.x + w, baseY);
      ctx.lineTo(p.x + w * 0.7, baseY - h);
      ctx.lineTo(p.x - w * 0.7, baseY - h);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = 'rgba(228, 232, 226, 0.75)';
      ctx.fillRect(p.x - w * 0.7, baseY - h, w * 1.4, Math.max(0.6, 0.9 * scale));
    }

    // The saplings. Thin, and shorter than the grass they are standing in — a
    // hedgerow that looked like a hedgerow on the day it was paid for would be the
    // one thing in this build that arrived before it grew.
    ctx.strokeStyle = 'rgba(104, 148, 96, 0.85)';
    ctx.lineWidth = Math.max(0.7, 1.1 * scale);
    for (const sapling of mercy.planted()) {
      const p = camera.gridToScreen(sapling.x, sapling.y);
      const h = Math.max(2, 3.6 * scale);
      for (let i = -1; i <= 1; i++) {
        const sx = p.x + i * Math.max(1, 2 * scale);
        ctx.beginPath();
        ctx.moveTo(sx, p.y + cellH * 0.06);
        ctx.lineTo(sx + i * Math.max(0.5, scale), p.y + cellH * 0.06 - h);
        ctx.stroke();
      }
    }

    ctx.restore();
  }
}
